import React, { useState } from 'react';
import { CheckIcon, XIcon } from 'lucide-react';
import { ATRIBUTOS_DOCUMENTO, CATEGORIAS_DOCUMENTO } from '../../data/tiposDocumento';
import { MATERIAS } from '../../data/catalogos';
import type { AtributoDocumento, CategoriaDocumento, Materia, TipoDocumento } from '../../types';
const claseInput = 'h-9 w-full rounded border border-gris-200 px-3 text-sm text-gris-800 placeholder:text-gris-400 focus:border-marino-500';
const claseEtiqueta = 'block text-2xs font-semibold uppercase tracking-wide text-gris-500';
export function EditorTipoDocumento({
  tipo,
  esNuevo,
  onGuardar,
  onCancelar
}: {
  tipo: TipoDocumento;
  esNuevo: boolean;
  onGuardar: (tipo: TipoDocumento) => void;
  onCancelar: () => void;
}) {
  const [borrador, setBorrador] = useState<TipoDocumento>(tipo);
  const [error, setError] = useState('');
  const cambiar = <C extends keyof TipoDocumento,>(campo: C, valor: TipoDocumento[C]) => setBorrador(prev => ({
    ...prev,
    [campo]: valor
  }));
  const alternarAtributo = (clave: AtributoDocumento) => setBorrador(prev => ({
    ...prev,
    atributos: prev.atributos.includes(clave) ? prev.atributos.filter(a => a !== clave) : [...prev.atributos, clave]
  }));
  const alternarMateria = (materia: Materia) => setBorrador(prev => ({
    ...prev,
    materias: prev.materias.includes(materia) ? prev.materias.filter(m => m !== materia) : [...prev.materias, materia]
  }));
  const guardar = () => {
    if (!borrador.nombre.trim()) {
      setError('El nombre del tipo de documento es obligatorio.');
      return;
    }
    if (!borrador.aplicaTodas && borrador.materias.length === 0) {
      setError('Selecciona al menos una materia o marca «Aplica en todas las materias».');
      return;
    }
    onGuardar({
      ...borrador,
      nombre: borrador.nombre.trim()
    });
  };
  return <div className="border-b border-marino-200 bg-marino-50 px-5 py-5" data-id="element-286" data-mp="1jhs43i4j9icp">
      <h2 className="text-sm font-semibold text-marino-900" data-id="element-287" data-mp="1jjlacwb8i0k6">
        {esNuevo ? 'Nuevo tipo de documento' : `Editar «${tipo.nombre}»`}
      </h2>

      <div className="mt-4 grid gap-4 md:grid-cols-2" data-id="element-288" data-mp="3oil2dctjb8r6">
        <label data-id="element-289" data-mp="3ol78r94ovi7t">
          <span className={claseEtiqueta} data-id="element-290" data-mp="3ti7efixmmiye">Nombre *</span>
          <input autoFocus className={`mt-1.5 ${claseInput}`} value={borrador.nombre} onChange={e => cambiar('nombre', e.target.value)} placeholder="Ej. Ofrecimiento de pruebas" data-id="element-291" data-mp="3thed4xhfslnn" />
        </label>
        <label data-id="element-292" data-mp="bc1ddbvhdeb7">
          <span className={claseEtiqueta} data-id="element-293" data-mp="bbjc20tyo9wf">Categoría *</span>
          <select className={`mt-1.5 ${claseInput}`} value={borrador.categoria} onChange={e => cambiar('categoria', e.target.value as CategoriaDocumento)} data-id="element-294" data-mp="g3b28a97gdm1">
            {CATEGORIAS_DOCUMENTO.map(c => <option key={c} value={c} data-id="element-295" data-mp="p5kdj2casb0h">
                {c}
              </option>)}
          </select>
        </label>
      </div>

      <fieldset className="mt-4 rounded border border-gris-200 bg-white px-4 py-3" data-id="element-296" data-mp="2el3gt12i4f62">
        <legend className={claseEtiqueta} data-id="element-297" data-mp="2eiqobtz4amer">Atributos que se exigen al subir el documento</legend>
        <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-3" data-id="element-298" data-mp="29o4jyargdu46">
          {ATRIBUTOS_DOCUMENTO.map(a => <label key={a.clave} className={`flex cursor-pointer items-start gap-2.5 rounded border px-3 py-2 transition-colors duration-150 ${borrador.atributos.includes(a.clave) ? 'border-marino-600 bg-marino-50' : 'border-gris-200 hover:border-gris-300'}`} data-id="element-299" data-mp="20lbxckt7u6ko">
              <input type="checkbox" checked={borrador.atributos.includes(a.clave)} onChange={() => alternarAtributo(a.clave)} className="mt-0.5 h-4 w-4 rounded border-gris-300 text-marino-700" data-id="element-300" data-mp="2oh6g7z5yxzpb" />
              <span className="min-w-0" data-id="element-301" data-mp="3c83islbvjknw">
                <span className="block text-xs font-medium text-gris-800" data-id="element-302" data-mp="3c70v0m9ulyc7">{a.etiqueta}</span>
                <span className="block text-2xs text-gris-400" data-id="element-303" data-mp="2vvp8rne7cbi5">{a.ayuda}</span>
              </span>
            </label>)}
        </div>
      </fieldset>

      <div className="mt-4 grid gap-4 md:grid-cols-2" data-id="element-304" data-mp="2mtzg6e8l36is">
        <fieldset className="rounded border border-gris-200 bg-white px-4 py-3" data-id="element-305" data-mp="2rrhgjg8jcdf6">
          <legend className={claseEtiqueta} data-id="element-306" data-mp="2rtlu6pw8kqsn">Reglas del tipo</legend>
          <label className="mt-1 flex items-start gap-2.5 text-sm text-gris-700" data-id="element-307" data-mp="3fhvc3gqbwuei">
            <input type="checkbox" checked={borrador.requiereVencimiento} onChange={e => cambiar('requiereVencimiento', e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-gris-300 text-marino-700" data-id="element-308" data-mp="3jhvpcy1oyzjz" />
            <span data-id="element-309" data-mp="2aa0ce1wcp0z">
              Requiere fecha de vencimiento
              <span className="block text-2xs text-gris-400" data-id="element-310" data-mp="28yy4io3npbz">
                Al subirlo se pide una fecha límite que entra a la agenda de términos.
              </span>
            </span>
          </label>
          <label className="mt-3 flex items-start gap-2.5 text-sm text-gris-700" data-id="element-311" data-mp="gdz9ycaf240c">
            <input type="checkbox" checked={borrador.confidencial} onChange={e => cambiar('confidencial', e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-gris-300 text-marino-700" data-id="element-312" data-mp="ccnsiu1p4p38" />
            <span data-id="element-313" data-mp="3tnww972a1hgw">
              Es confidencial
              <span className="block text-2xs text-gris-400" data-id="element-314" data-mp="3toosmk3p7j88">
                Visibilidad restringida al abogado asignado y al Administrador.
              </span>
            </span>
          </label>
        </fieldset>

        <fieldset className="rounded border border-gris-200 bg-white px-4 py-3" data-id="element-315" data-mp="2gu5ggqcpd77">
          <legend className={claseEtiqueta} data-id="element-316" data-mp="2ix5zup4qoq6">Materias en las que aplica</legend>
          <label className="mt-1 flex items-center gap-2.5 text-sm text-gris-700" data-id="element-317" data-mp="1iszkgddxdaqd">
            <input type="checkbox" checked={borrador.aplicaTodas} onChange={e => cambiar('aplicaTodas', e.target.checked)} className="h-4 w-4 rounded border-gris-300 text-marino-700" data-id="element-318" data-mp="1dv7q7cpt5cj0" />
            Aplica en todas las materias
          </label>
          {!borrador.aplicaTodas && <div className="mt-3 flex flex-wrap gap-2" data-id="element-319" data-mp="1zvy9aw6tz1rq">
              {MATERIAS.map(m => {
            const activo = borrador.materias.includes(m);
            return <button key={m} type="button" onClick={() => alternarMateria(m)} aria-pressed={activo} className={`h-8 rounded-full border px-3 text-xs transition-colors duration-150 ${activo ? 'border-marino-800 bg-marino-800 font-medium text-white' : 'border-gris-200 bg-white text-gris-600 hover:border-gris-300'}`} data-id="element-320" data-mp="177iwmgb68vjk">
                    {m}
                  </button>;
          })}
            </div>}
        </fieldset>
      </div>

      {error && <p role="alert" className="mt-3 text-xs font-medium text-peligro-600" data-id="element-321" data-mp="bwl3vw7tnipp">
          {error}
        </p>}

      <div className="mt-4 flex justify-end gap-2" data-id="element-322" data-mp="20hzejphz7zl">
        <button type="button" onClick={onCancelar} className="inline-flex h-9 items-center gap-1.5 rounded border border-gris-200 bg-white px-3 text-sm font-medium text-gris-600 transition-colors duration-150 hover:border-gris-300" data-id="element-323" data-mp="1zq7biv3zse2">
          <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-324" data-mp="3kfahjx66feu5" />
          Cancelar
        </button>
        <button type="button" onClick={guardar} className="inline-flex h-9 items-center gap-1.5 rounded bg-marino-800 px-3.5 text-sm font-semibold text-white transition-colors duration-150 hover:bg-marino-700" data-id="element-325" data-mp="3ffxkykpi6ted">
          <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-326" data-mp="36gkgk6g8kv0i" />
          {esNuevo ? 'Agregar al catálogo' : 'Guardar cambios'}
        </button>
      </div>
    </div>;
}